/* PZR-MC-A03 — Shopify-native Making in Motion runtime. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const collectSections = (root, callback) => {
    if (root?.matches?.('[data-pzr-motion-native]')) callback(root);
    root?.querySelectorAll?.('[data-pzr-motion-native]').forEach(callback);
  };

  const initializeSection = (section) => {
    if (section.dataset.pzrMotionReady === 'true') return;

    const rail = section.querySelector('[data-pzr-motion-rail]');
    const previous = section.querySelector('[data-pzr-motion-prev]');
    const next = section.querySelector('[data-pzr-motion-next]');
    const cards = [...section.querySelectorAll('.pzr-motion-card')];
    if (!rail || cards.length === 0) return;

    const entries = cards
      .map((card) => ({
        card,
        video: card.querySelector('video'),
        toggle: card.querySelector('[data-pzr-motion-toggle]'),
        userPaused: false
      }))
      .filter((entry) => entry.video && entry.toggle);

    if (entries.length === 0) {
      section.hidden = true;
      return;
    }

    section.dataset.pzrMotionReady = 'true';

    const setState = (entry, playing) => {
      entry.card.classList.toggle('is-playing', playing);
      entry.toggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
      const label = entry.toggle.dataset.pzrMotionLabel || 'making video';
      entry.toggle.setAttribute('aria-label', `${playing ? 'Pause' : 'Play'} ${label}`);
    };

    const pauseEntry = (entry) => {
      entry.video.pause();
      setState(entry, false);
    };

    const pauseOthers = (current) => {
      entries.forEach((entry) => {
        if (entry !== current) pauseEntry(entry);
      });
    };

    const getStep = () => {
      const firstCard = entries.find((entry) => !entry.card.hidden)?.card;
      const styles = getComputedStyle(rail);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return (firstCard?.getBoundingClientRect().width || rail.clientWidth * 0.75) + gap;
    };

    const updateNavigation = () => {
      const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const canScroll = maxScroll > 2;
      if (previous) {
        previous.hidden = !canScroll;
        previous.disabled = !canScroll || rail.scrollLeft <= 2;
      }
      if (next) {
        next.hidden = !canScroll;
        next.disabled = !canScroll || rail.scrollLeft >= maxScroll - 2;
      }
    };

    const tryPlay = async (entry, { manual = false } = {}) => {
      if (reduceMotion.matches || document.hidden || entry.card.hidden) return;
      if (!manual && entry.userPaused) return;
      if (manual) entry.userPaused = false;
      pauseOthers(entry);
      try {
        await entry.video.play();
        setState(entry, true);
      } catch (_) {
        setState(entry, false);
      }
    };

    entries.forEach((entry) => {
      entry.video.muted = true;
      entry.video.loop = true;
      entry.video.playsInline = true;
      entry.video.preload = 'metadata';
      setState(entry, false);

      entry.toggle.addEventListener('click', () => {
        if (entry.video.paused) {
          entry.userPaused = false;
          void tryPlay(entry, { manual: true });
        } else {
          entry.userPaused = true;
          pauseEntry(entry);
        }
      });

      entry.video.addEventListener('play', () => {
        pauseOthers(entry);
        setState(entry, true);
      });
      entry.video.addEventListener('pause', () => setState(entry, false));
      entry.video.addEventListener('ended', () => setState(entry, false));
      entry.video.addEventListener('error', () => {
        entry.card.hidden = true;
        if (entries.every((item) => item.card.hidden)) section.hidden = true;
        updateNavigation();
      });
    });

    const scrollRail = (direction) => {
      rail.scrollBy({
        left: direction * getStep(),
        behavior: reduceMotion.matches ? 'auto' : 'smooth'
      });
    };

    previous?.addEventListener('click', () => scrollRail(-1));
    next?.addEventListener('click', () => scrollRail(1));
    rail.addEventListener('scroll', updateNavigation, { passive: true });

    const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(updateNavigation) : null;
    resizeObserver?.observe(rail);
    updateNavigation();

    let observer = null;
    const stopAutoplay = () => entries.forEach(pauseEntry);

    if (!reduceMotion.matches && 'IntersectionObserver' in window) {
      const visibility = new Map(entries.map((entry) => [entry.card, 0]));
      observer = new IntersectionObserver((observed) => {
        observed.forEach((item) => {
          visibility.set(item.target, item.isIntersecting ? item.intersectionRatio : 0);
        });

        if (reduceMotion.matches || document.hidden) {
          stopAutoplay();
          return;
        }

        const best = entries
          .filter((entry) => !entry.card.hidden && !entry.userPaused)
          .map((entry) => ({ entry, ratio: visibility.get(entry.card) || 0 }))
          .filter((item) => item.ratio >= 0.62)
          .sort((a, b) => b.ratio - a.ratio)[0];

        if (!best) {
          entries.forEach((entry) => {
            if (!entry.userPaused) pauseEntry(entry);
          });
          return;
        }

        if (best.entry.video.paused) void tryPlay(best.entry);
      }, { threshold: [0, 0.62, 0.8] });

      entries.forEach((entry) => observer.observe(entry.card));
    }

    const onReduceMotion = () => {
      if (reduceMotion.matches) stopAutoplay();
    };
    const onVisibility = () => {
      if (document.hidden) stopAutoplay();
    };
    const onPageHide = () => stopAutoplay();

    reduceMotion.addEventListener?.('change', onReduceMotion);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);

    section.pzrMotionCleanup = () => {
      observer?.disconnect();
      resizeObserver?.disconnect();
      reduceMotion.removeEventListener?.('change', onReduceMotion);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      stopAutoplay();
      delete section.dataset.pzrMotionReady;
      delete section.pzrMotionCleanup;
    };
  };

  const destroySection = (section) => section.pzrMotionCleanup?.();

  collectSections(document, initializeSection);
  document.addEventListener('shopify:section:load', (event) => collectSections(event.target, initializeSection));
  document.addEventListener('shopify:section:unload', (event) => collectSections(event.target, destroySection));
})();
